import { useState } from "react"
import { router } from "@inertiajs/react"
import { csrfToken } from "@/lib/csrf"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { DateTimePicker } from "@/components/ui/date-time-picker"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import PlatformCaptionTabs from "@/components/dashboard/posts/platform-caption-tabs"
import {
  INITIAL_TIKTOK_STATE,
  isTiktokStateValid,
  type TiktokPostState,
} from "@/lib/tiktok-post-state"
import type { LocationMeta } from "@/components/dashboard/posts/location-picker"
import type Render from "@/types/serializers/Render"
import type Listing from "@/types/serializers/Listing"
import type SocialConnection from "@/types/serializers/SocialConnection"

type Platform = "instagram" | "facebook" | "tiktok" | "youtube"

const PLATFORM_LABELS: Record<Platform, string> = {
  instagram: "Instagram",
  facebook: "Facebook",
  tiktok: "TikTok",
  youtube: "YouTube",
}

const CAPTION_LIMITS: Partial<Record<Platform, number>> = {
  instagram: 2000,
}

interface AdminPostDialogProps {
  videoId: string
  renders: Render[]
  listing: Listing
  connections: SocialConnection[]
  open: boolean
  onOpenChange: (open: boolean) => void
}

export default function AdminPostDialog({
  videoId,
  renders,
  listing,
  connections,
  open,
  onOpenChange,
}: AdminPostDialogProps) {
  const completedRenders = renders.filter((r) => r.status === "completed" && r.video_url)
  const hasVideo = completedRenders.length > 0
  const hasImages = listing.images_data.length > 0
  const isVideo = hasVideo

  const [selectedRenderId, setSelectedRenderId] = useState<string>(
    completedRenders[0]?.id || "",
  )
  const [selectedImageSignedId, setSelectedImageSignedId] = useState<string>(
    listing.images_data[0]?.signed_id || "",
  )
  const [selectedPlatforms, setSelectedPlatforms] = useState<Set<Platform>>(new Set())
  const [captions, setCaptions] = useState<Record<Platform, string>>({
    instagram: "",
    facebook: "",
    tiktok: "",
    youtube: "",
  })
  const [facebookPageId, setFacebookPageId] = useState<string>("")
  const [facebookPostType, setFacebookPostType] = useState<"reel" | "video">("reel")
  const [shareToFeed, setShareToFeed] = useState(true)
  const [tiktokState, setTiktokState] = useState<TiktokPostState>(INITIAL_TIKTOK_STATE)
  const [generatingCaption, setGeneratingCaption] = useState<Record<string, boolean>>({})
  const [locationId, setLocationId] = useState<string | null>(null)
  const [locationName, setLocationName] = useState<string | null>(null)
  const [locationMeta, setLocationMeta] = useState<LocationMeta | null>(null)
  const [scheduleEnabled, setScheduleEnabled] = useState(false)
  const [scheduledAt, setScheduledAt] = useState<Date | undefined>(undefined)
  const [publishing, setPublishing] = useState(false)

  const fbConnection = connections.find(
    (c) => c.platform === "facebook" && c.status === "active",
  )
  const fbPages = fbConnection?.managed_pages || []

  const allowedPlatforms: Platform[] = isVideo
    ? ["instagram", "facebook", "tiktok", "youtube"]
    : ["instagram", "facebook"]

  const activeConnections = connections.filter(
    (c) => allowedPlatforms.includes(c.platform as Platform) && c.status === "active",
  )

  function togglePlatform(platform: Platform) {
    setSelectedPlatforms((prev) => {
      const next = new Set(prev)
      if (next.has(platform)) {
        next.delete(platform)
      } else {
        next.add(platform)
      }
      return next
    })
  }

  function resetForm() {
    setSelectedPlatforms(new Set())
    setCaptions({ instagram: "", facebook: "", tiktok: "", youtube: "" })
    setScheduleEnabled(false)
    setScheduledAt(undefined)
    setFacebookPageId("")
    setFacebookPostType("reel")
    setShareToFeed(true)
    setTiktokState(INITIAL_TIKTOK_STATE)
    setSelectedRenderId(completedRenders[0]?.id || "")
    setSelectedImageSignedId(listing.images_data[0]?.signed_id || "")
    setLocationId(null)
    setLocationName(null)
    setLocationMeta(null)
  }

  async function handleGenerateCaption(platform: string) {
    setGeneratingCaption((prev) => ({ ...prev, [platform]: true }))
    try {
      const response = await fetch("/admin/posts/generate_caption", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken(),
        },
        body: JSON.stringify({ platform, listing_id: listing.id, video_id: videoId }),
      })

      if (response.ok) {
        const data = await response.json()
        const limit = CAPTION_LIMITS[platform as Platform]
        const caption = limit ? (data.caption as string).slice(0, limit) : data.caption
        setCaptions((prev) => ({ ...prev, [platform]: caption }))
      } else {
        const data = await response.json()
        toast.error(data.error || "Failed to generate caption")
      }
    } catch {
      toast.error("Something went wrong. Please try again.")
    } finally {
      setGeneratingCaption((prev) => ({ ...prev, [platform]: false }))
    }
  }

  async function handlePublish() {
    if (selectedPlatforms.size === 0) return

    setPublishing(true)
    try {
      const body: Record<string, unknown> = {
        post_type: isVideo ? "video" : "image",
        platforms: Array.from(selectedPlatforms),
        captions: Object.fromEntries(
          Array.from(selectedPlatforms).map((p) => [p, captions[p]]),
        ),
      }

      if (isVideo) {
        body.render_id = selectedRenderId
      } else {
        body.listing_id = listing.id
        body.listing_image_signed_id = selectedImageSignedId
      }

      if (scheduleEnabled && scheduledAt) {
        body.scheduled_at = scheduledAt.toISOString()
      }
      if (selectedPlatforms.has("facebook") && facebookPageId) {
        body.facebook_page_id = facebookPageId
      }
      if (selectedPlatforms.has("facebook")) {
        body.facebook_post_type = facebookPostType
      }
      if (selectedPlatforms.has("instagram")) {
        body.share_to_feed = shareToFeed
      }
      if (selectedPlatforms.has("tiktok")) {
        body.tiktok_privacy = tiktokState.privacy
        body.tiktok_disable_comment = !tiktokState.allowComments
        body.tiktok_disable_duet = !tiktokState.allowDuet
        body.tiktok_disable_stitch = !tiktokState.allowStitch
        body.tiktok_brand_content_toggle = tiktokState.brandedContent
        body.tiktok_brand_organic_toggle = tiktokState.yourBrand
      }
      if (locationId) body.location_id = locationId
      if (locationName) body.location_name = locationName
      if (locationMeta?.address) body.location_address = locationMeta.address
      if (locationMeta?.verified) body.location_verified = "true"
      if (locationMeta?.picture_url) body.location_picture_url = locationMeta.picture_url
      if (locationMeta?.hours) body.location_hours = locationMeta.hours

      const response = await fetch("/admin/posts", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken(),
        },
        body: JSON.stringify(body),
      })

      if (response.ok) {
        toast.success(
          scheduleEnabled
            ? "Posts scheduled successfully"
            : selectedPlatforms.has("tiktok")
              ? "Publishing to social media. TikTok may take a few minutes to process your video."
              : "Publishing to social media...",
        )
        onOpenChange(false)
        resetForm()
        router.reload()
      } else {
        const data = await response.json()
        toast.error(data.error || "Failed to create posts")
      }
    } catch {
      toast.error("Something went wrong. Please try again.")
    } finally {
      setPublishing(false)
    }
  }

  const hasConnections = activeConnections.length > 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="dark max-h-[85vh] overflow-y-auto border-white/10 bg-charcoal sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-cream">New {isVideo ? "video" : "image"} post</DialogTitle>
          <DialogDescription className="text-cream/50">
            {isVideo
              ? "Publish this video to your connected accounts."
              : "Publish a listing image to your connected accounts."}
          </DialogDescription>
        </DialogHeader>

        {!hasConnections ? (
          <div className="rounded-lg border border-white/10 bg-white/5 px-4 py-5 text-center">
            <svg className="mx-auto mb-2 size-8 text-cream/30" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" />
            </svg>
            <p className="text-sm font-medium text-cream">No social accounts connected</p>
            <p className="mt-1 text-xs text-cream/50">
              Connect your social media accounts to start publishing.
            </p>
            <a
              href="/dashboard/connections"
              className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-red px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-deep-red"
            >
              Connect accounts
            </a>
          </div>
        ) : (
          <>
            {/* Render selector (video, multiple renders) */}
            {isVideo && completedRenders.length > 1 && (
              <div className="space-y-2 py-2">
                <p className="text-[11px] font-semibold uppercase tracking-widest text-cream/40">
                  Render
                </p>
                <Select value={selectedRenderId} onValueChange={setSelectedRenderId}>
                  <SelectTrigger className="border-white/10 bg-white/5 text-cream">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="dark border-white/10 bg-charcoal">
                    {completedRenders.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.template_variant} — {new Date(r.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Image selector (image mode) */}
            {!isVideo && hasImages && (
              <div className="space-y-2 py-2">
                <p className="text-[11px] font-semibold uppercase tracking-widest text-cream/40">
                  Image
                </p>
                <div className="grid grid-cols-4 gap-2">
                  {listing.images_data.map((img) => (
                    <button
                      key={img.signed_id}
                      type="button"
                      onClick={() => setSelectedImageSignedId(img.signed_id)}
                      className={`overflow-hidden rounded-lg border-2 transition-all ${
                        selectedImageSignedId === img.signed_id
                          ? "border-red"
                          : "border-transparent opacity-60 hover:opacity-100"
                      }`}
                    >
                      <img
                        src={img.thumb_url}
                        alt=""
                        className="aspect-square w-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Platform selection */}
            <div className="space-y-3 py-2">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-cream/40">
                Platforms
              </p>
              <div className="grid grid-cols-2 gap-2">
                {allowedPlatforms.map((platform) => {
                  const connection = activeConnections.find(
                    (c) => c.platform === platform,
                  )
                  const isSelected = selectedPlatforms.has(platform)
                  const disabled = !connection

                  return (
                    <button
                      key={platform}
                      type="button"
                      onClick={() => !disabled && togglePlatform(platform)}
                      disabled={disabled}
                      className={`flex items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left text-sm transition-all ${
                        disabled
                          ? "cursor-not-allowed border-white/5 bg-white/[0.02] text-cream/20"
                          : isSelected
                            ? "border-red/50 bg-red/10 text-cream"
                            : "border-white/10 text-cream/50 hover:border-white/20"
                      }`}
                    >
                      <span
                        className={`flex size-4 items-center justify-center rounded border text-[10px] ${
                          isSelected
                            ? "border-red bg-red text-white"
                            : "border-white/20"
                        }`}
                      >
                        {isSelected && (
                          <svg className="size-2.5" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                          </svg>
                        )}
                      </span>
                      <span className="font-medium">
                        {PLATFORM_LABELS[platform]}
                      </span>
                      {!connection && (
                        <span className="ml-auto text-[10px] text-cream/30">
                          Not connected
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Captions with PlatformCaptionTabs — location, generate, copy-to-all, FB page */}
            {selectedPlatforms.size > 0 && (
              <PlatformCaptionTabs
                platforms={Array.from(selectedPlatforms)}
                captions={captions}
                onCaptionChange={(platform, value) => {
                  const limit = CAPTION_LIMITS[platform as Platform]
                  const capped = limit ? value.slice(0, limit) : value
                  setCaptions((prev) => ({ ...prev, [platform]: capped }))
                }}
                onGenerateCaption={handleGenerateCaption}
                generatingCaption={generatingCaption}
                facebookPages={fbPages}
                facebookPageId={facebookPageId}
                onFacebookPageChange={setFacebookPageId}
                facebookPostType={isVideo ? facebookPostType : undefined}
                onFacebookPostTypeChange={isVideo ? setFacebookPostType : undefined}
                shareToFeed={isVideo ? shareToFeed : undefined}
                onShareToFeedChange={isVideo ? setShareToFeed : undefined}
                locationId={locationId}
                locationName={locationName}
                locationMeta={locationMeta}
                onLocationChange={(id, name, meta) => {
                  setLocationId(id)
                  setLocationName(name)
                  setLocationMeta(meta ?? null)
                }}
                lat={listing.latitude}
                lng={listing.longitude}
                tiktokConnectionId={
                  selectedPlatforms.has("tiktok")
                    ? activeConnections.find((c) => c.platform === "tiktok")?.id
                    : null
                }
                tiktokState={tiktokState}
                onTiktokStateChange={setTiktokState}
              />
            )}

            {/* Schedule toggle */}
            {selectedPlatforms.size > 0 && (
              <div className="space-y-3 border-t border-white/10 pt-4">
                <div className="flex items-center gap-2">
                  <Switch
                    id="admin-post-schedule-toggle"
                    checked={scheduleEnabled}
                    onCheckedChange={setScheduleEnabled}
                  />
                  <Label htmlFor="admin-post-schedule-toggle" className="text-sm text-cream">
                    Schedule for later
                  </Label>
                </div>

                {scheduleEnabled && (
                  <DateTimePicker
                    value={scheduledAt}
                    onChange={setScheduledAt}
                    minDate={new Date()}
                    placeholder="Choose date & time"
                    className="w-auto"
                  />
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="ghost" className="text-cream/50 hover:text-cream" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={handlePublish}
                disabled={
                  publishing ||
                  selectedPlatforms.size === 0 ||
                  (isVideo && !selectedRenderId) ||
                  (!isVideo && !selectedImageSignedId) ||
                  (scheduleEnabled && !scheduledAt) ||
                  (selectedPlatforms.has("tiktok") && !isTiktokStateValid(tiktokState))
                }
                className="bg-red px-6 text-white hover:bg-red/90"
              >
                {publishing
                  ? "Publishing..."
                  : scheduleEnabled
                    ? "Schedule"
                    : `Publish to ${selectedPlatforms.size} platform${selectedPlatforms.size !== 1 ? "s" : ""}`}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
